
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
// Add ResizeObserver patch to fix loop warning
import '../../utils/patchResizeObserver';
import ASNDetailsTab from "./tabs/ASNDetailsTab";
import ShipFromTab from "./tabs/ShipFromTab";
import CarrierTab from "./tabs/CarrierTab";
import LoadTab from "./tabs/LoadTab";
import UserDefinedTab from "./tabs/UserDefinedTab";
import InvoiceTab from "./tabs/InvoiceTab";
import { useSelector, useDispatch } from "react-redux";
import { setLoadTable } from "../../redux/action/skuTable.action";
import SKUSearchResults from "./SKUSearchResults";
import { sanitizeFormData } from "../../utils/sanitizeFormData";

import { addItemsFieldsData } from "./formFields/asnTabsFields";
import LineItemsTable from "./LineItemsTable";

import {
  buildDefaultASNFormData,
  asnDetailsFields,
  supplierAddressFieldsASN,
  buyerAddressFieldsASN,
  carrierFields,
  loadFields,
  userDefinedFields,
  invoiceFields,
  isFieldVisible
} from "./formFields/asnTabsFields";

import { createANS, updateASN, confirmASN, getSearchBySkuPO } from "../../services/asnAPI/asn";
// IMPORT: project's helper (from your reference form)
import { fetchDataAndPatchFields, getSkuDetailsById } from "../../services/item/item";

import { toast } from '../../components/AppToast';
import SerialCaptureModal, { SerialCaptureButton } from "../../components/SerialCaptureModal";
import {
  compactSerialRows,
  countCapturedSerials,
  getSerialConfig,
  resolveSerialColumns,
} from "../../utils/serialCapture";
import { formatToInputDate } from "../../utils/dateFormatter";

import ConfirmDialog from "../../components/ConfirmDialog";

export default function CreateASNTabs({
  initialValues = {},
  onCancel,
  onSubmit = () => { },
  isEdit = false,
  viewOnly = false,
  isAddDetailsMode = false
}) {
  // map readOnly used in inner item form code to viewOnly
  const readOnly = !!viewOnly;

  const [activeTab, setActiveTab] = useState(isAddDetailsMode ? 2 : 0);

  const [formData, setFormData] = useState(() => {
    const v = { ...buildDefaultASNFormData(), ...initialValues };
    const toDate = (x) => formatToInputDate(x, false) || "";
    const toDateTime = (x) => formatToInputDate(x, true) || "";

    const createdDate = v.asnCreatedate ?? v.createdAt ?? new Date().toISOString().slice(0, 10);
    return {
      ...v,
      asnType: v.asnType || "Normal",
      asnCreatedate: toDateTime(v.asnCreatedate ?? v.createdAt ?? v.asnDetails?.asnCreatedate),
      createdAt: toDateTime(v.createdAt),
      closedDate: toDate(v.closedDate ?? v.asnDetails?.closedDate),
      expectedDepartureDate: toDate(v.expectedDepartureDate ?? v.load?.expectedDepartureDate),
      actualDepartureDate: toDate(v.actualDepartureDate ?? v.load?.actualArrivalDate),
      expectedArrivalDate: toDate(v.expectedArrivalDate ?? v.load?.expectedArrivalDate),
      actualArrivalDate: toDate(v.actualArrivalDate ?? v.load?.actualArrivalDate),
      invoiceDate: toDate(v.invoiceDate ?? createdDate),
    };
  });

  const loadTable = useSelector(state => state.skutable.TableDetails);
  const loadTableRef = useRef(loadTable);
  loadTableRef.current = loadTable;
  const dispatch = useDispatch();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [skuToRemove, setSkuToRemove] = useState(null);

  const [selectedRowsData, setSelectedRowsData] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmissionAttempted, setFormSubmissionAttempted] = useState(false);

  const tabs = [
    { label: "ASN Header", component: ASNDetailsTab },
    { label: "Address", component: ShipFromTab },
    { label: "Carrier", component: CarrierTab },
    { label: "Load/Dates", component: LoadTab },
    { label: "Invoice", component: InvoiceTab },
    { label: "User Defined", component: UserDefinedTab },
  ];

  // ---------- BLIND add-item state ----------
  const [showItemForm, setShowItemForm] = useState(false);
  const [itemFormData, setItemFormData] = useState({});
  const [itemErrors, setItemErrors] = useState({});
  const [editingItem, setEditingItem] = useState(null); // { groupKey, index }
  const itemFirstInputRef = useRef(null);
  const [lineNumber, setLineNumber] = useState("");


  useEffect(() => {
    const num = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };

    const calcAsnTotal = (row) => {
      const qty = num(row?.dispatchQty);
      const unit = num(row?.asnUnitPrice);
      const disc = num(row?.asnDiscount);
      const tax = num(row?.tax ?? row?.Tax);

      if (qty === 0 || unit === 0) return 0;

      const subtotal = qty * unit;
      const taxedSubtotal = subtotal * (1 + tax / 100);
      return taxedSubtotal * (1 - disc / 100);
    };

    let grandTotal = 0;

    Object.entries(loadTable || {}).forEach(([groupKey, rows]) => {
      if (groupKey.startsWith("__")) return;
      if (!Array.isArray(rows)) return;

      rows.forEach((r) => {
        const pre = num(r?.asnTotalPrice);
        grandTotal += pre > 0 ? pre : calcAsnTotal(r);
      });
    });

    const nextVal = grandTotal.toFixed(2);

    if ((formData?.invoiceAmount ?? "") !== nextVal) {
      handleChange({ target: { name: "invoiceAmount", value: nextVal } });
    }
  }, [loadTable]);


  const handleSelectionChange = useCallback((selectedRowsDataBySku) => {
    // normalize empty -> null
    if (!selectedRowsDataBySku || Object.keys(selectedRowsDataBySku).length === 0) {
      setSelectedRowsData(null);
      return;
    }
    setSelectedRowsData(selectedRowsDataBySku);
  }, []);

  const blankItem = () => ({
    lineNumber: "",
    item: "",
    skuId: "",
    description: "",
    pack: "STD",
    packKey: "STD",
    lpnNo: "",
    batchNum: "",
    manufacturingDate: "",
    expirationDate: "",
    itemcode: "",
    itemweight: "",
    uom: "EA",
    orderQty: "",
    tolerance: "",
    qcRequired: false,
    openqty: "",
    receivedqty: "",
    status: "New",
    serialConfig: null,
    serialDetails: [],
  });

  // ---------- Serial / IMEI capture (inbound: icdflag) ----------
  // target: { kind: "blind" } for the Blind item form, or { kind: "row", groupKey, index } for search-result rows
  const [serialTarget, setSerialTarget] = useState(null);
  const skuSerialConfigCache = useRef({});

  const loadSerialConfig = useCallback(async (skuId) => {
    const key = String(skuId || "").trim();
    if (!key) return null;
    if (!(key in skuSerialConfigCache.current)) {
      skuSerialConfigCache.current[key] = getSkuDetailsById(key)
        .then((detail) => (detail ? getSerialConfig(detail) : null))
        .catch((err) => {
          console.warn("Serial config lookup failed for", key, err);
          return null;
        });
    }
    return skuSerialConfigCache.current[key];
  }, []);

  useEffect(() => {
    if (!isEdit && !viewOnly) {
      dispatch(setLoadTable({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defensive: ensure _id from initialValues is present in formData when in edit mode
  useEffect(() => {
    if (isEdit && initialValues && (initialValues._id || initialValues.asnNumber)) {
      setFormData(prev => {
        // only set if missing to avoid overwriting user edits
        if (prev._id || prev.asnNumber) return prev;
        return { ...prev, _id: initialValues._id, asnNumber: initialValues.asnNumber || prev.asnNumber };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initialValues]);

  // hydrate from localStorage for edit/view
  useEffect(() => {
    if (!(isEdit || viewOnly)) return;
    if (!formData.asnNumber) return;
    if (loadTable && Object.keys(loadTable).length > 0) return;
    try {
      const list = JSON.parse(localStorage.getItem("asnList") || "[]");
      if (!Array.isArray(list)) return;
      const rec = list.find(r => r.asnDetails?.asnNumber === formData.asnNumber);
      if (!rec || !Array.isArray(rec.items)) return;
      const tableMap = {};
      rec.items.forEach(it => {
        const hasPoGroup = Object.prototype.hasOwnProperty.call(it, 'poNumber') && it.poNumber;
        const hasSkuGroup = Object.prototype.hasOwnProperty.call(it, 'skuid') && it.skuid;
        let key;
        if (hasPoGroup) key = `PO::${it.poNumber}`;
        else if (hasSkuGroup) key = `SKU::${it.skuid}`;
        else return;
        tableMap[key] = (it.poDetails || []).map((d, idx) => ({
          ...d,
          lineNo: d.lineNo ?? d.rowIndex ?? (idx + 1),
          item: d.item || d.skuId || it.skuid || it.poNumber,
          skuId: d.skuId || d.item || it.skuid,
        }));
      });
      if (Object.keys(tableMap).length) {
        dispatch(setLoadTable(tableMap));
      }
    } catch {
      /* silent */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, viewOnly, formData.asnNumber]);

  const handleChange = (e) => {
    if (viewOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === 'createUsing') {
        const numericValue = isNaN(value) ? value : Number(value);
        return {
          ...prev,
          createUsing: numericValue,
          SKUID: numericValue === 1 ? prev.SKUID : '',
          poNumber: numericValue === 2 ? prev.poNumber : '',
          externalPoNumber: numericValue === 3 ? prev.externalPoNumber : ''
        };
      }
      return { ...prev, [name]: value };
    });
  };

  // ---------- helper: fetch SKU/PO and return patch for item form (try project's fetchDataAndPatchFields first) ----------
  const fetchDataAndPatchFieldForSo = async (fieldOrName, value) => {
    const fieldName = typeof fieldOrName === "string" ? fieldOrName : (fieldOrName && fieldOrName.name) || "skuId";
    if (!value) return null;

    // 1) Try project's fetchDataAndPatchFields (preferred)
    try {
      if (typeof fetchDataAndPatchFields === "function") {
        const patchFromService = await fetchDataAndPatchFields(fieldName, value);
        if (patchFromService && typeof patchFromService === "object" && Object.keys(patchFromService).length) {
          return patchFromService;
        }
      }
    } catch (err) {
      console.warn("fetchDataAndPatchFields failed:", err);
      // fall through to fallback
    }

    // 2) Fallback to existing getSearchBySkuPO
    try {
      const q = { skuId: String(value).trim() };
      const resp = await getSearchBySkuPO(q);

      let row = null;
      if (Array.isArray(resp) && resp.length) row = resp[0];
      else if (resp && Array.isArray(resp.results) && resp.results.length) row = resp.results[0];
      else if (resp && resp.data && (Array.isArray(resp.data) ? resp.data[0] : resp.data)) {
        row = Array.isArray(resp.data) ? resp.data[0] : resp.data;
      } else if (resp && typeof resp === 'object') {
        row = resp.item || resp;
      }

      if (!row) return null;

      const patch = {};
      patch.skuId = row.skuId || row.sku || row.SKU || String(value).trim();
      patch.item = patch.skuId;

      const pickFirst = (obj, candidates) => {
        for (const k of candidates) {
          if (obj && (obj[k] !== undefined && obj[k] !== null && obj[k] !== "")) return obj[k];
        }
        return undefined;
      };

      const desc = pickFirst(row, ['description', 'desc', 'name', 'title']);
      if (desc !== undefined) patch.description = desc;

      const code = pickFirst(row, ['itemcode', 'itemCode', 'code', 'skuCode', 'sku_code']);
      if (code !== undefined) patch.itemcode = code;

      const weight = pickFirst(row, ['itemweight', 'weight', 'netWeight']);
      if (weight !== undefined) patch.itemweight = weight;

      const uom = pickFirst(row, ['uom', 'unit', 'unitOfMeasure']);
      if (uom !== undefined) patch.uom = uom;
      const pack = pickFirst(row, ['pack', 'packType']);
      if (pack !== undefined) patch.pack = pack;

      const qty = pickFirst(row, ['expectedQty', 'orderQty', 'qty', 'quantity']);
      if (qty !== undefined) patch.orderQty = qty;

      const tol = pickFirst(row, ['tolerance', 'tolerancePercent']);
      if (tol !== undefined) patch.tolerance = tol;

      if (row.barcode) patch.barcode = row.barcode;

      return patch;
    } catch (err) {
      console.error('fetchDataAndPatchFieldForSo error (fallback):', err);
      return null;
    }
  };

  // validation helpers (kept minimal here)
  const validateForm = () => {
    const allFieldGroups = [
      { fields: asnDetailsFields, tabIndex: 0, tabName: "ASN Header" },
      { fields: supplierAddressFieldsASN, tabIndex: 1, tabName: "Address" },
      { fields: carrierFields, tabIndex: 2, tabName: "Carrier" },
      { fields: loadFields, tabIndex: 3, tabName: "Load/Dates" },
      { fields: invoiceFields, tabIndex: 4, tabName: "Invoice" },
      { fields: userDefinedFields, tabIndex: 5, tabName: "User Defined" },
    ];

    const errors = [];

    for (const group of allFieldGroups) {
      for (const field of group.fields) {
        if (field.hiddenOnCreate || field.readOnly || !field.required) continue;
        if (!isFieldVisible(field, formData)) continue;
        const value = formData[field.name];
        const isEmpty = value === null || value === undefined || value === '' ||
          (typeof value === 'string' && value.trim() === '');
        if (isEmpty) {
          errors.push({
            fieldName: field.name,
            fieldLabel: field.label,
            tabIndex: group.tabIndex,
            tabName: group.tabName
          });
        }
      }
    }
    return errors;
  };

  const focusOnField = (fieldName) => {
    setTimeout(() => {
      const fieldElement = document.getElementById(fieldName) || document.querySelector(`[name="${fieldName}"]`);
      if (fieldElement) {
        fieldElement.focus();
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldElement.style.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.25)';
        fieldElement.style.borderColor = '#dc3545';
        setTimeout(() => {
          fieldElement.style.boxShadow = '';
          fieldElement.style.borderColor = '';
        }, 3000);
      }
    }, 100);
  };

  // helper: pick fields by definition array
  const pickFields = (src, fieldsArr) => {
    const set = new Set(fieldsArr.map(f => f.name));
    const out = {};
    Object.keys(src || {}).forEach(k => {
      if (set.has(k)) out[k] = src[k];
    });
    return out;
  };

  // helper: ISO date normalizer
  function toIsoDate(val) {
    if (!val) return null;
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    if (typeof val === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
      const [day, month, year] = val.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
    return null;
  }

  const normalizeSearchResultRow = (r) => ({
    ...r,
    packKey: 'STD',
    lpnNo: r.lpnNo || r.lpn || r['LPN No'] || r['LPN'] || '',
    batchNum: r.batchNum || r.batchNo || r.batchNumber || r['Batch Num'] || r['Batch No'] || '',
    manufacturingDate: r.manufacturingDate || r['Manufacturing Date'] || r.manufacturingdate || '',
    expirationDate: r.expirationDate || r['Expiration Date'] || r.expirationdate || '',
  });

  // ---------- function to search SKU / PO and populate loadTable ----------
  const searchAndPopulate = async ({ by = "sku", value = "" }) => {
    if (!value) return;
    try {
      const q = by === "sku" ? { skuId: value } : { poNumber: value };
      const resp = await getSearchBySkuPO(q);

      const updated = { ...(loadTable || {}) };

      // remove non-BLIND groups to replace with search result
      Object.keys(updated).forEach(k => {
        if (!k.startsWith('__') && k !== 'BLIND') delete updated[k];
      });

      const rows = Array.isArray(resp?.groups) ? resp.groups :
        (Array.isArray(resp) ? resp : (Array.isArray(resp?.results) ? resp.results : []));

      if (by === "po") {
        const poKey = `PO::${value}`;
        updated[poKey] = (rows || []).map((r, idx) => ({
          ...normalizeSearchResultRow(r),
          lineNo: r.lineNo ?? String(idx + 1).padStart(4, '0'),
          skuId: r.skuId || r.item || '',
          item: r.skuId || r.item || '',
          purchaseOrder: value,
          poNumber: value,
        }));
        updated.__lastAddedTable = poKey;
      } else {
        const skuKey = `SKU::${value}`;
        updated[skuKey] = (rows || []).map((r, idx) => ({
          ...normalizeSearchResultRow(r),
          lineNo: r.lineNo ?? String(idx + 1).padStart(4, '0'),
          skuId: r.skuId || value,
          item: r.skuId || value,
        }));
        updated.__lastAddedTable = skuKey;
      }

      updated.__userTriggered = true;
      dispatch(setLoadTable(updated));
    } catch (err) {
      console.error("searchAndPopulate error:", err);
    }
  };

  const attachSerialConfigs = async () => {
    const pending = new Set();
    Object.entries(loadTableRef.current || {}).forEach(([k, rows]) => {
      if (k.startsWith("__") || k === "BLIND" || !Array.isArray(rows)) return;
      rows.forEach((r) => {
        const sku = r?.skuId || r?.item;
        if (sku && r.serialConfig === undefined) pending.add(String(sku).trim());
      });
    });
    if (!pending.size) return;

    const configs = Object.fromEntries(
      await Promise.all([...pending].map(async (sku) => [sku, await loadSerialConfig(sku)]))
    );

    const current = loadTableRef.current || {};
    const next = { ...current };
    let changed = false;
    Object.entries(current).forEach(([k, rows]) => {
      if (k.startsWith("__") || k === "BLIND" || !Array.isArray(rows)) return;
      next[k] = rows.map((r) => {
        const sku = String(r?.skuId || r?.item || "").trim();
        if (r.serialConfig !== undefined || !(sku in configs)) return r;
        changed = true;
        return { ...r, serialConfig: configs[sku] };
      });
    });
    if (changed) {
      loadTableRef.current = next;
      dispatch(setLoadTable(next));
    }
  };

  useEffect(() => {
    attachSerialConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTable]);

  useEffect(() => {
    if (Number(formData.createUsing) === 1 && formData.SKUID) {
      searchAndPopulate({ by: "sku", value: formData.SKUID });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.SKUID]);

  useEffect(() => {
    if (Number(formData.createUsing) === 2 && formData.poNumber) {
      searchAndPopulate({ by: "po", value: formData.poNumber });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.poNumber]);

  // ---------- submit handler ----------
  const handleSubmit = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (viewOnly) { onCancel && onCancel(); return; }

    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormSubmissionAttempted(true);

    // ---------- choose data source: selectedRowsData (if any) else entire loadTable ----------
    let sourceMap = {};

    // if user has selected rows (from UI), prefer that as source
    const hasSelections = selectedRowsData && Object.values(selectedRowsData).some(arr => Array.isArray(arr) && arr.length > 0);

    if (hasSelections) {
      // Selected rows can be a snapshot from before the date inputs were edited.
      // Overlay the latest loadTable row so manufacturing/expiration dates are kept.
      sourceMap = Object.fromEntries(
        Object.entries(selectedRowsData).map(([k, rows]) => {
          const latest = Array.isArray(loadTable?.[k]) ? loadTable[k] : [];
          const mapped = (Array.isArray(rows) ? rows : []).map((r, idx) => {
            const rowIndex = typeof r?.rowIndex === 'number' ? r.rowIndex : idx;
            const fresh = latest[rowIndex] || r || {};
            return { rowIndex, skuId: fresh.skuId || fresh.item || r?.skuId, ...fresh };
          });
          return [k, mapped];
        })
      );
    } else {
      // fallback: use entire loadTable as before
      sourceMap = Object.fromEntries(
        Object.entries(loadTable || {})
          .filter(([key, val]) => key !== '__lastAddedTable' && key !== '__userTriggered' && Array.isArray(val) && val.length > 0)
          .map(([groupKey, rows]) => [
            groupKey,
            Array.isArray(rows) ? rows.map((r, idx) => ({ rowIndex: idx, skuId: r.skuId || r.item, ...r })) : []
          ])
      );
    }

    // Flatten to savedData
    const savedData = [];
    Object.values(sourceMap || {}).forEach(arr => {
      if (Array.isArray(arr)) savedData.push(...arr);
    });

    // DEBUG: log mode and id
    console.log("handleSubmit: isEdit=", isEdit, "formData._id=", formData._id, "formData.asnNumber=", formData.asnNumber);

    // When in Add Details mode validate only carrier/load
    if (isAddDetailsMode) {
      const carrierErrors = [];
      const loadErrors = [];
      carrierFields.forEach(field => {
        if (field.required && !field.readOnly) {
          const value = formData[field.name];
          const isEmpty = value === null || value === undefined || value === '' ||
            (typeof value === 'string' && value.trim() === '');
          if (isEmpty) carrierErrors.push(field.label);
        }
      });
      loadFields.forEach(field => {
        if (field.required && !field.readOnly) {
          const value = formData[field.name];
          const isEmpty = value === null || value === undefined || value === '' ||
            (typeof value === 'string' && value.trim() === '');
          if (isEmpty) loadErrors.push(field.label);
        }
      });
      if (carrierErrors.length > 0 || loadErrors.length > 0) {
        const allErrors = [...carrierErrors, ...loadErrors];
        toast.error(
          `Please fill in required fields: ${allErrors.slice(0, 3).join(', ')}${allErrors.length > 3 ? '...' : ''}`,
          { position: "top-center", autoClose: 5000 }
        );
        setIsSubmitting(false);
        return;
      }
    } else {
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        const firstError = validationErrors[0];
        const errorFieldsList = validationErrors.slice(0, 3).map(err => err.fieldLabel).join(', ');
        const moreErrors = validationErrors.length > 3 ? ` and ${validationErrors.length - 3} more` : '';
        toast.error(`Please fill required fields: ${errorFieldsList}${moreErrors}`);
        if (firstError.tabIndex !== activeTab) setActiveTab(firstError.tabIndex);
        focusOnField(firstError.fieldName);
        setIsSubmitting(false);
        return;
      }
    }

    const salesOrderId = formData.asnNumber || formData._id || null;

    // ---------- Build flat lineItems ALWAYS (even for createUsing === 3) ----------
    // helper to prefer first non-nullish value
    const pick = (...vals) => {
      for (const v of vals) { if (v !== undefined && v !== null) return v; }
      return undefined;
    };
    const pickFilled = (...vals) => {
      for (const v of vals) {
        if (v === undefined || v === null) continue;
        if (typeof v === 'string' && v.trim() === '') continue;
        return v;
      }
      return undefined;
    };

    const createUsingVal = Number(formData.createUsing);

    const finalLineItems = savedData.map(({ id, rowIndex, lineNo, lineNumber, detail = {}, ...rest }, idx) => {
      const skuId = pick(rest.skuId, rest.item, detail.skuId, "");
      const purchaseOrder = pick(rest.purchaseOrder, rest.poNumber, detail.purchaseOrder, null);
      const orderedQty = Number(pick(rest.expectedQty, rest.orderQty, rest.orderedQty, detail.orderedQty, 0)) || 0;
      const description = pick(rest.description, detail.description, "") || "";
      const pack = (() => {
        const v = pick(rest.pack, detail.pack, "STD");
        return (v === "" || v === undefined || v === null) ? "STD" : v;
      })();
      const uom = pick(rest.uom, detail.uom, "EA") || "EA";
      const ln = pick(rest.lineNumber, lineNo, detail.lineNumber, String(idx + 1).padStart(5, "0"));

      const receiptLineNumber = String(idx + 1).padStart(5, "0");

      return {
        skuId,
        lineItemId: rest.lineItemId || null,
        purchaseOrder,
        purchaseOrderId: rest.purchaseOrderId || null,
        orderedQty,
        description,
        pack,
        packKey: rest.packKey || "",
        lpnNo: pick(rest.lpnNo, rest.lpn, detail.lpnNo, detail.lpn, rest.__originalLine__?.lpnNo, "") || "",
        batchNum: pick(rest.batchNum, rest.batchNo, rest.batchNumber, detail.batchNum, detail.batchNo, detail.batchNumber, rest.__originalLine__?.batchNum, "") || "",
        manufacturingDate: toIsoDate(pickFilled(
          rest.manufacturingDate,
          rest.__originalLine__?.manufacturingDate,
          detail.manufacturingDate,
          rest.manufacturingdate,
          rest['Manufacturing Date']
        )),
        expirationDate: toIsoDate(pickFilled(
          rest.expirationDate,
          rest.__originalLine__?.expirationDate,
          detail.expirationDate,
          rest.expirationdate,
          rest.expiryDate,
          rest['Expiration Date']
        )),
        uom,
        openQty: rest.openQty || 0,
        shortQty: rest.shortQty || 0,
        excessQty: rest.excessQty || 0,
        damagedQty: rest.damagedQty || 0,
        dispatchQty: rest.dispatchQty || 0,
        lineNumber: ln,
        receiptLineNumber,
        rowIndex: (typeof rowIndex === 'number') ? rowIndex : idx,
        itemPOTolerance:  rest.itemPOTolerance,     // mapped from tolerance/tolerancePercent/detail.tolerancePercent
        asnDiscount: rest.asnDiscount || 0,
        asnUnitPrice: rest.asnUnitPrice || 0,
        asnTotalPrice: rest.asnTotalPrice || 0,
        unitPrice: rest.unitPrice || 0,
        discount: rest.discount || 0,
        tax: rest.tax || 0,
        poTotalPrice: rest.poTotalPrice || 0,
        totalPrice: rest.totalPrice || 0,
        serialDetails: compactSerialRows(
          rest.serialDetails,
          Number(rest.dispatchQty) > 0 ? rest.dispatchQty : orderedQty
        )
      };
    });

    // Validation: ensure at least one item (flat always)
    // if (!Array.isArray(finalLineItems) || finalLineItems.length === 0) {
    //   toast.error("Please add at least one valid item before creating the ASN");
    //   if (activeTab !== 0) setActiveTab(0);
    //   setIsSubmitting(false);
    //   return;
    // }

    // ---------- Prepare other payload parts ----------
    const asnDetails = { ...pickFields(formData, asnDetailsFields) };

    if (!asnDetails.asnCreatedate) {
      if (isEdit) {
        try {
          const list = JSON.parse(localStorage.getItem("asnList") || "[]");
          const existing = list.find(r => r.asnDetails?.asnNumber === asnDetails?.asnNumber);
          if (existing?.asnDetails?.asnCreatedate) {
            asnDetails.asnCreatedate = existing.asnDetails.asnCreatedate;
          } else {
            asnDetails.asnCreatedate = new Date().toISOString().slice(0, 10);
          }
        } catch {
          asnDetails.asnCreatedate = new Date().toISOString().slice(0, 10);
        }
      } else {
        asnDetails.asnCreatedate = new Date().toISOString().slice(0, 10);
      }
    }
    asnDetails.asnCreatedate = toIsoDate(asnDetails.asnCreatedate);
    asnDetails.createdAt = toIsoDate(asnDetails.createdAt);
    asnDetails.closedDate = toIsoDate(asnDetails.closedDate);

    const load = { ...pickFields(formData, loadFields) };
    load.expectedDepartureDate = toIsoDate(load.expectedDepartureDate);
    load.actualDepartureDate = toIsoDate(load.actualDepartureDate);
    load.expectedArrivalDate = toIsoDate(load.expectedArrivalDate);
    load.actualArrivalDate = toIsoDate(load.actualArrivalDate);

    const invoice = { ...pickFields(formData, invoiceFields) };
    invoice.invoiceDate = toIsoDate(invoice.invoiceDate);

    const payload = {
      asnDetails,
      shipFrom: pickFields(formData, supplierAddressFieldsASN),
      // `owner` kept on shipTo for backward compatibility with the previous payload shape.
      shipTo: { ...pickFields(formData, buyerAddressFieldsASN), owner: formData.owner ?? "" },
      carrier: pickFields(formData, carrierFields),
      load,
      invoice,
      userDefined: pickFields(formData, userDefinedFields),
      lineItems: Array.isArray(finalLineItems) ? finalLineItems : []
    };

    // Log for debugging — verify shape before submitting
    console.log("Submitting ASN payload (createUsing:", createUsingVal, "):", payload);

    try {
      let response;
      const hasId = !!formData._id;
      const hasAsnNumber = !!formData.asnNumber;
      const shouldUpdate = Boolean(isEdit && (hasId || hasAsnNumber));

      if (shouldUpdate) {
        let idToUpdate = formData._id || null;
        if (!idToUpdate && formData.asnNumber) {
          try {
            console.info("Resolving _id for asnNumber:", formData.asnNumber);
            const getAsnByNumberUrl = `/api/asn/search?asnNumber=${encodeURIComponent(formData.asnNumber)}`;
            const r = await fetch(getAsnByNumberUrl, {
              method: "GET",
              headers: { "Content-Type": "application/json" }
            });
            const json = await r.json();
            const found = Array.isArray(json) && json.length ? json[0] : (json && (json.data || json.item || json));
            idToUpdate = found?._id || found?.id || null;
            console.info("Resolved id for update:", idToUpdate, "raw response:", json);
          } catch (err) {
            console.warn("Failed to resolve ASN by number:", err);
          }
        }

        if (!idToUpdate) {
          toast.error("Cannot update ASN: missing identifier (_id). Please open the ASN from list or ensure the ASN record has an _id.", { autoClose: 7000 });
          setIsSubmitting(false);
          return;
        }

        const payloadToUpdate = sanitizeFormData ? sanitizeFormData(payload) : payload;
        // delete payloadToUpdate.asnDetails.receiptStatus;
        console.log("Submitting ASN update payload:", payloadToUpdate); 
        
        console.debug("Calling updateASN with id:", idToUpdate, "payload:", payloadToUpdate);
        try {
          response = await updateASN(idToUpdate, payloadToUpdate);
          console.info("updateASN response:", response);
          const asnNum = response?.asnNumber || asnDetails?.asnNumber || formData.asnNumber || formData._id;
          toast.success(`ASN ${asnNum} ${isAddDetailsMode ? 'submitted' : 'updated'} successfully`, { position: "top-center", autoClose: 3000 });
        } catch (err) {
          console.error("updateASN server error:", err);
          const serverMsg = err?.response?.data || err?.message || JSON.stringify(err);
          toast.error(`Failed to update ASN: ${serverMsg}`, { autoClose: 8000 });
          setIsSubmitting(false);
          return;
        }
      } else {
        if (isEdit && !hasId && !hasAsnNumber) {
          toast.error("Cannot update ASN: missing identifier (_id or asnNumber).", { autoClose: 5000 });
          setIsSubmitting(false);
          return;
        }

        response = await createANS(payload);
        
        if (response && response.asnNumber) {
          toast.success(`ASN Number ${response.asnNumber} created successfully`, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          toast.success('ASN created successfully', { position: 'top-center', autoClose: 3000 });
        }
      }

      setFormSubmissionAttempted(false);
      onSubmit(response);
    } catch (error) {
      console.error("ASN submit error:", error);
      const msg = (error && (error.message || (error.data && error.data.message))) || "Unknown error";
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} ASN: ${msg}`, { autoClose: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmAsn = async () => {

    console.log("handleConfirmAsn called with formData:");
    if (!formData?.asnNumber) return;
    try {
      const payload = { asnNumber: formData.asnNumber };
      await confirmASN(payload);
      toast.success(`ASN ${formData.asnNumber} confirmed successfully`);
      onCancel && onCancel();
    } catch (err) {
      // toast.error("Confirm failed");
    }
  };

  const getTabValidationStatus = (tabIndex) => {
    if (!formSubmissionAttempted) return false;
    const fieldGroups = [
      asnDetailsFields,
      supplierAddressFieldsASN,
      carrierFields,
      loadFields,
      invoiceFields,
      userDefinedFields,
    ];
    const fields = fieldGroups[tabIndex] || [];
    return fields.some(field => {
      if (!field.required || field.hiddenOnCreate || field.readOnly) return false;
      if (!isFieldVisible(field, formData)) return false;
      const value = formData[field.name];
      return value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === '');
    });
  };

  // ---------- BLIND handlers ----------
  const openAddItemForm = () => {
    const updated = { ...(loadTable || {}) };
    if (!Array.isArray(updated.BLIND)) updated.BLIND = [];
    updated.__lastAddedTable = "BLIND";
    updated.__userTriggered = true;
    dispatch(setLoadTable(updated));

    const nextLine = updated.BLIND.length + 1;
    setLineNumber(String(nextLine).padStart(4, '0'));
    setItemFormData({ ...blankItem(), lineNumber: String(nextLine).padStart(4, '0') });
    setItemErrors({});
    setEditingItem(null);
    setShowItemForm(true);

    setTimeout(() => {
      if (itemFirstInputRef.current) itemFirstInputRef.current.focus();
      const el = document.getElementById('items-table-anchor');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  // === REPLACED handleItemChange with user's version (uses readOnly variable) ===
  const handleItemChange = (e) => {
    const target = e && e.target ? e.target : e;
    const { name, value, type, checked } = target || {};
    if (readOnly) return;

    setItemFormData((prev) => {
      const val = type === "checkbox" ? checked : value;

      // Normalize: keep 'item' and 'skuId' in sync
      if (name === "item" || name === "skuId") {
        const nextSku = val == null ? "" : String(val);
        const skuChanged = nextSku !== (prev.skuId ?? prev.item ?? "");
        return {
          ...prev,
          item: nextSku,
          skuId: nextSku,
          ...(skuChanged ? { serialConfig: null, serialDetails: [] } : {}),
        };
      }

      return { ...prev, [name]: val };
    });

    // errors cleanup
    setItemErrors((prev) => {
      if (!prev || Object.keys(prev).length === 0) return {};
      const clone = { ...prev };
      if (name in clone) {
        delete clone[name];
        return clone;
      }
      const currentLine = itemFormData.lineNumber || null;
      if (currentLine && clone[currentLine]) {
        const rowErr = { ...clone[currentLine] };
        if (name in rowErr) {
          delete rowErr[name];
          if (Object.keys(rowErr).length === 0) {
            delete clone[currentLine];
          } else {
            clone[currentLine] = rowErr;
          }
        }
      }
      return clone;
    });
  };

  const validateItemForm = () => {
    const errs = {};
    addItemsFieldsData.forEach(f => {
      if (f.required) {
        const v = itemFormData[f.name];
        const empty = v === null || v === undefined || v === "" || (typeof v === 'string' && v.trim() === "");
        if (empty) errs[f.name] = `${f.label} is required`;
      }
    });
    return errs;
  };

  const addOrUpdateItemToTable = () => {
    const errs = validateItemForm();
    if (Object.keys(errs).length) {
      setItemErrors(errs);
      toast.error("Please fill all required fields");
      return;
    }

    const expectedQty = Number(itemFormData.orderQty) || 0;
    const serialRequired =
      !!itemFormData.serialConfig?.inboundEnabled && expectedQty > 0;
    if (serialRequired) {
      const cols = resolveSerialColumns(
        itemFormData.serialConfig?.inboundLabels,
        itemFormData.serialDetails
      );
      const captured = countCapturedSerials(
        itemFormData.serialDetails,
        expectedQty,
        cols
      );
      if (captured < expectedQty) {
        toast.error(
          `Please fill all required Serial / IMEI details (${captured}/${expectedQty}) before adding the item`
        );
        setSerialTarget({ kind: "blind" });
        return;
      }
    }

    const groupKey = "BLIND";
    const updated = { ...(loadTable || {}) };
    if (!Array.isArray(updated[groupKey])) updated[groupKey] = [];
    const row = {
      lineNo: itemFormData.lineNumber || String(updated[groupKey].length + 1).padStart(4, "0"),
      item: itemFormData.item || itemFormData.skuId || "",
      skuId: itemFormData.skuId || itemFormData.item || "",
      description: itemFormData.description || "",
      pack: itemFormData.pack || "STD",
      packKey: itemFormData.packKey || "",
      lpnNo: itemFormData.lpnNo || "",
      batchNum: itemFormData.batchNum || "",
      manufacturingDate: itemFormData.manufacturingDate || "",
      expirationDate: itemFormData.expirationDate || "",
      uom: itemFormData.uom || "EA",
      expectedQty,
      orderQty: expectedQty,
      openQty: expectedQty,
      dispatchQty: 0,
      qcRequired: !!itemFormData.qcRequired,
      receivedQty: 0,
      receivedDate: null,
      status: itemFormData.status || "New",
      serialConfig: itemFormData.serialConfig || null,
      serialDetails: compactSerialRows(itemFormData.serialDetails, expectedQty),
      __originalLine__: { ...itemFormData },
    };

    if (editingItem && editingItem.groupKey && typeof editingItem.index === 'number') {
      updated[editingItem.groupKey] = [...(updated[editingItem.groupKey] || [])];
      updated[editingItem.groupKey][editingItem.index] = row;
      toast.success("Item updated");
    } else {
      updated[groupKey] = [...updated[groupKey], row];
      toast.success("Item added");
    }

    updated.__lastAddedTable = groupKey;
    updated.__userTriggered = true;
    dispatch(setLoadTable(updated));

    // Keep Item Detail form open after Add (same as PO) so next lines can be entered
    const nextLine = (updated[groupKey]?.length || 0) + 1;
    const padded = String(nextLine).padStart(4, "0");
    setLineNumber(padded);
    setItemFormData({ ...blankItem(), lineNumber: padded });
    setItemErrors({});
    setEditingItem(null);
    setShowItemForm(true);

    setTimeout(() => {
      if (itemFirstInputRef.current) itemFirstInputRef.current.focus();
      const el = document.getElementById('items-table-anchor');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const handleEditItem = (row) => {
    const groupKey = row.__groupKey;
    const idx = row.__index;
    const sourceRow = (loadTable?.[groupKey] && loadTable[groupKey][idx]) || row;
    setItemFormData({
      ...blankItem(),
      lineNumber: sourceRow.lineNo ?? sourceRow.lineNumber ?? String(idx + 1).padStart(4, '0'),
      item: sourceRow.skuId ?? sourceRow.item ?? '',
      skuId: sourceRow.skuId ?? sourceRow.item ?? '',
      description: sourceRow.description ?? '',
      pack: sourceRow.pack ?? 'STD',
      packKey: sourceRow.packKey ?? '',
      lpnNo: sourceRow.lpnNo ?? sourceRow.lpn ?? '',
      batchNum: sourceRow.batchNum ?? sourceRow.batchNo ?? sourceRow.batchNumber ?? '',
      manufacturingDate: sourceRow.manufacturingDate ?? '',
      expirationDate: sourceRow.expirationDate ?? '',
      uom: sourceRow.uom ?? 'EA',
      orderQty: sourceRow.expectedQty ?? 0,
      tolerance: sourceRow.tolerance ?? '',
      qcRequired: !!sourceRow.qcRequired,
      itemcode: sourceRow.itemcode ?? '',
      itemweight: sourceRow.itemweight ?? '',
      receivedqty: sourceRow.receivedQty ?? 0,
      openqty: sourceRow.openQty ?? 0,
      status: sourceRow.status ?? 'New',
      serialConfig: sourceRow.serialConfig ?? null,
      serialDetails: Array.isArray(sourceRow.serialDetails) ? sourceRow.serialDetails : [],
    });
    setEditingItem({ groupKey, index: idx });
    setShowItemForm(true);

    setTimeout(() => {
      if (itemFirstInputRef.current) itemFirstInputRef.current.focus();
      const el = document.getElementById('items-table-anchor');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const handleDeleteItem = (row) => {
    const groupKey = row?.__groupKey;
    const idx = row?.__index;
    if (groupKey === undefined || idx === undefined) {
      toast.error("Unable to delete (invalid identifier)");
      return;
    }
    const updated = { ...(loadTable || {}) };
    if (!Array.isArray(updated[groupKey])) {
      toast.error("Group not found");
      return;
    }
    const arr = [...updated[groupKey]];
    arr.splice(idx, 1);
    if (arr.length === 0) delete updated[groupKey];
    else updated[groupKey] = arr;
    updated.__lastAddedTable = undefined;
    updated.__userTriggered = true;
    dispatch(setLoadTable(updated));
    toast.success("Item removed");
  };

  // flattened rows for LineItemsTable - unique ids (include group key to avoid duplicate keys)
  const flattenedLoadRows = useMemo(() => {
    const out = [];
    if (!loadTable || typeof loadTable !== 'object') return out;
    Object.keys(loadTable).forEach(k => {
      if (k.startsWith('__')) return;
      const arr = loadTable[k];
      if (!Array.isArray(arr)) return;
      arr.forEach((r, idx) => {
        const rawLine = r.lineNo ?? r.lineNumber ?? String(idx + 1).padStart(4, '0');
        const uniqueId = `${k}::${r.id ?? rawLine ?? `row-${idx}`}`;
        out.push({
          ...r,
          __groupKey: k,
          __index: idx,
          id: uniqueId,
          lineNumber: rawLine,
        });
      });
    });
    return out;
  }, [loadTable]);

  // SKU line-item table is only for Blind ASN rows. Do not render it empty
  // just because createUsing is Blind while the lines were grouped elsewhere.
  const shouldShowLineItems = useMemo(() => {
    return !!(loadTable && Array.isArray(loadTable.BLIND) && loadTable.BLIND.length > 0);
  }, [loadTable]);

  const blindSerialEnabled =
    !!itemFormData.serialConfig?.inboundEnabled || (itemFormData.serialDetails?.length ?? 0) > 0;
  const blindSerialColumns = useMemo(
    () => resolveSerialColumns(itemFormData.serialConfig?.inboundLabels, itemFormData.serialDetails),
    [itemFormData.serialConfig, itemFormData.serialDetails]
  );

  const serialModalProps = useMemo(() => {
    if (!serialTarget) return null;
    if (serialTarget.kind === "blind") {
      return {
        qty: itemFormData.orderQty,
        rows: itemFormData.serialDetails,
        columns: blindSerialColumns,
        skuId: itemFormData.skuId || itemFormData.item,
        readOnly: false,
      };
    }
    const row = loadTable?.[serialTarget.groupKey]?.[serialTarget.index];
    if (!row) return null;
    return {
      qty: row.expectedQty ?? row.orderQty ?? row.dispatchQty,
      rows: row.serialDetails,
      columns: resolveSerialColumns(row.serialConfig?.inboundLabels, row.serialDetails),
      skuId: row.skuId || row.item,
      readOnly: !!serialTarget.preview,
    };
  }, [serialTarget, itemFormData, blindSerialColumns, loadTable]);

  const handleSerialSave = (rows) => {
    if (serialTarget?.kind === "blind") {
      setItemFormData((prev) => ({ ...prev, serialDetails: rows }));
    } else if (serialTarget && !serialTarget.preview) {
      handleDispatchQtyChange(serialTarget.groupKey, serialTarget.index, rows, "serialDetails");
    }
    setSerialTarget(null);
  };

  const handlePreviewSerials = (row) => {
    if (!row || row.__groupKey === undefined || row.__index === undefined) return;
    const hasSerials =
      (Array.isArray(row.serialDetails) && row.serialDetails.length > 0) ||
      !!row.serialConfig?.inboundEnabled;
    if (!hasSerials) return;
    setSerialTarget({
      kind: "row",
      groupKey: row.__groupKey,
      index: row.__index,
      preview: true,
    });
  };

  // Focus first input ref helper render usage
  const firstInputRender = (name) => (
    <input
      ref={itemFirstInputRef}
      id={`item-${name}`}
      name={name}
      value={itemFormData[name] ?? ""}
      onChange={handleItemChange}
      className={`form-control ${itemErrors[name] ? "is-invalid" : ""}`}
    />
  );

  // Open the Blind item form when createUsing is Blind.
  // Do not reset the form when loadTable changes — that was clearing
  // manufacturing and expiration dates before they were added to the line.
  useEffect(() => {
    const isBlindMode = Number(formData.createUsing) === 3 && !viewOnly;
    if (!isBlindMode) {
      setShowItemForm(false);
      return;
    }

    const currentTable = loadTableRef.current;
    const tableObject = currentTable && !Array.isArray(currentTable) ? currentTable : {};
    if (!Array.isArray(tableObject.BLIND)) {
      dispatch(setLoadTable({
        ...tableObject,
        BLIND: [],
        __userTriggered: true,
      }));
    }

    setShowItemForm(true);
    setItemFormData(prev => {
      const hasStarted = prev && (
        prev.skuId || prev.item || prev.description || prev.manufacturingDate || prev.expirationDate || prev.orderQty
      );
      if (hasStarted) return prev;
      const blindLen = Array.isArray(tableObject.BLIND) ? tableObject.BLIND.length : 0;
      const padded = String(blindLen + 1).padStart(4, '0');
      setLineNumber(padded);
      return { ...blankItem(), lineNumber: padded };
    });
  }, [formData.createUsing, viewOnly, dispatch]);

  // dispatch qty change wrapper (calls your earlier logic)
  const handleDispatchQtyChange = (skuId, index, value, fieldName = 'dispatchQty') => {
    const currentTable = loadTableRef.current;
    const updatedResults = { ...(currentTable && !Array.isArray(currentTable) ? currentTable : {}) };
    updatedResults[skuId] = [...(updatedResults[skuId] || [])];

    updatedResults[skuId][index] = {
      ...updatedResults[skuId][index],
      [fieldName]: value
    };

    if (fieldName === 'dispatchQty' || fieldName === 'asnUnitPrice' || fieldName === 'asnDiscount') {
      const row = updatedResults[skuId][index];
      const dispatchQty = parseFloat(fieldName === 'dispatchQty' ? value : row.dispatchQty) || "";
      const asnUnitPrice = parseFloat(fieldName === 'asnUnitPrice' ? value : (row.asnUnitPrice)) || "";
      const asnDiscount = parseFloat(fieldName === 'asnDiscount' ? value : (row.asnDiscount)) || "";
      const tax = parseFloat(row.tax) || 0;

      const subtotal = dispatchQty * asnUnitPrice;
      const taxedSubtotal = subtotal * (1 + tax / 100);
      const asnTotalPrice = taxedSubtotal * (1 - asnDiscount / 100);

      updatedResults[skuId][index].asnTotalPrice = asnTotalPrice;

      const poUnitPrice = parseFloat(row.unitPrice) || 0;
      const poDiscount = parseFloat(row.discount) || 0;
      const poTax = parseFloat(row.tax) || 0;

      const poSubtotal = dispatchQty * poUnitPrice;
      const poTaxedSubtotal = poSubtotal * (1 + poTax / 100);
      const poTotalPrice = poTaxedSubtotal * (1 - poDiscount / 100);

      updatedResults[skuId][index].poTotalPrice = poTotalPrice;
    }

    loadTableRef.current = updatedResults;
    dispatch(setLoadTable(updatedResults));
  };

  const hasNonBlindGroups = useMemo(() => {
    if (!loadTable || typeof loadTable !== "object") return false;
    return Object.keys(loadTable).some(k => !k.startsWith("__") && k !== "BLIND");
  }, [loadTable]);


   // ✅ Hide Short/Excess/Damage/Received qty columns only while creating ASN by PO
  const hideQtyColumnsForCreateByPO =
    !isEdit &&
    !viewOnly &&
    (
      Number(formData.createUsing) === 2 || // PO
      Number(formData.createUsing) === 1    // SKU
    );


  return (
    <>
      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={() => {
          if (skuToRemove) {
            const updated = { ...(loadTable || {}) };
            delete updated[skuToRemove];
            dispatch(setLoadTable(updated));
            setSkuToRemove(null);
          }
          setOpenConfirm(false);
        }}
        title="Delete Item?"
        message="Do you really want to delete this item/group?"
        yesText="Yes"
        noText="No"
      />

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {tabs.map((tab, index) => {
          const hasErrors = getTabValidationStatus(index);
          const isCarrierTab = index === 2;
          const isLoadTab = index === 3;
          const isDisabled = isAddDetailsMode && !isCarrierTab && !isLoadTab;
          return (
            <li className="nav-item" key={index}>
              <button
                className={`nav-link ${activeTab === index ? "active" : ""} ${hasErrors ? "text-danger" : ""} ${isDisabled ? "disabled" : ""}`}
                onClick={() => !isDisabled && setActiveTab(index)}
                type="button"
                disabled={isDisabled}
                style={{
                  position: 'relative',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                {tab.label}
                {hasErrors && <span className="badge bg-danger rounded-pill ms-1" style={{ fontSize: '0.6em' }}>!</span>}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Active tab */}
      {(() => {
        // compute readOnly for the currently rendered tab
        const creating = !isEdit && !viewOnly;
        const tabLabel = tabs[activeTab]?.label || "";
        const readOnlyForTab = (() => {
          // view-only mode => all tabs readOnly
          if (viewOnly) return true;
          // add-details mode => only Carrier (2) and Load (3) editable (keep existing behavior)
          if (isAddDetailsMode) return activeTab !== 2 && activeTab !== 3;
          // edit mode => make Load/Dates read-only (same as create mode)
          if (isEdit) return tabLabel === "Load/Dates";
          // create mode => make Load/Dates read-only
          if (creating) return tabLabel === "Load/Dates";
          // default => not readOnly
          return false;
        })();

        return React.createElement(tabs[activeTab].component, {
          formData,
          onChange: handleChange,
          formSubmissionAttempted,
          readOnly: readOnlyForTab,
        });
      })()}

      <hr />

      {/* Anchor for scrolling */}
      <div id="items-table-anchor" />

      {/* Inline Add/Edit Item Form */}
      {!viewOnly && showItemForm && (
        <>
          <ul className="nav nav-tabs mb-3">
            {[{ name: 'Item Details', fields: ['lineNumber', 'item', 'skuId', 'description', 'itemcode'] }].map((tab, i) => (
              <li className="nav-item" key={tab.name}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === i ? "active" : ""}`}
                  onClick={() => setActiveTab(i)}
                >
                  {tab.name}
                </button>
              </li>
            ))}
          </ul>

          <div >
            <div className="card-body">
              <div className="row g-3 mx-0">
                {addItemsFieldsData.map((field, idx) => {
                  const isCheckbox = field.type === "checkbox";
                  const colClass = "col-12 col-sm-6 col-md-2";
                  const value = itemFormData[field.name] ?? field.defaultValue ?? "";
                  const attachRef = (idx === 0 ? itemFirstInputRef : null);

                  return (
                    <div className={colClass} key={field.name} autoComplete="off">
                      {field.name === "qcRequired" ? (
                        <>
                          <label className="form-label" htmlFor={`item-${field.name}`}>
                            {field.label}
                          </label>
                          <select
                            id={`item-${field.name}`}
                            className="form-select"
                            name={field.name}
                            value={itemFormData[field.name] ? "true" : "false"}
                            onChange={(e) =>
                              handleItemChange({
                                target: {
                                  name: field.name,
                                  value: e.target.value === "true",
                                },
                              })
                            }
                            disabled={readOnly}
                          >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        </>
                      ) : isCheckbox ? (
                        <div className="form-check mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`item-${field.name}`}
                            name={field.name}
                            checked={!!itemFormData[field.name]}
                            onChange={handleItemChange}
                            disabled={readOnly}
                          />
                          <label className="form-check-label" htmlFor={`item-${field.name}`}>
                            {field.label}
                          </label>
                        </div>
                      ) : field.name === "lineNumber" ? (
                        <>
                          <label className="form-label" htmlFor={`item-${field.name}`}>
                            {field.label}
                          </label>
                          <input
                            id={`item-${field.name}`}
                            type="text"
                            className="form-control bg-light"
                            name={field.name}
                            value={itemFormData.lineNumber || lineNumber}
                            readOnly
                          />
                        </>
                      ) : field.type === "select" ? (
                        <>
                          <label className="form-label" htmlFor={`item-${field.name}`}>
                            {field.label} {field.required && <span className="text-danger">*</span>}
                          </label>
                          <select
                            id={`item-${field.name}`}
                            className={`form-select ${field.readOnly ? "bg-light" : ""} ${itemErrors[field.name] ? "is-invalid" : ""}`}
                            name={field.name}
                            value={itemFormData[field.name] || ""}
                            onChange={handleItemChange}
                            disabled={field.readOnly || readOnly}
                          >
                            {(field.options || []).map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          {itemErrors[field.name] && (
                            <div className="invalid-feedback d-block">
                              {itemErrors[field.name]}
                            </div>
                          )}
                        </>
                      ) : field.hasSearch ? (
                        <>
                          <label className="form-label" htmlFor={`item-${field.name}`}>
                            {field.label} {field.required && <span className="text-danger">*</span>}
                          </label>
                          <input
                            id={`item-${field.name}`}
                            type={field.type}
                            ref={attachRef}
                            className={`form-control ${(field.readOnly || readOnly) ? "bg-light" : ""} ${itemErrors[field.name] ? "is-invalid" : ""}`}
                            name={field.name}
                            value={
                              field.name === "skuId"
                                ? itemFormData.skuId ?? itemFormData.item ?? ""
                                : itemFormData[field.name] ?? ""
                            }
                            onChange={handleItemChange}
                            readOnly={field.readOnly || readOnly}
                            onKeyDown={async (e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const currentVal =
                                  field.name === "skuId"
                                    ? itemFormData.skuId ?? itemFormData.item ?? ""
                                    : itemFormData[field.name] ?? "";
                                const result = await fetchDataAndPatchFieldForSo(field.name, currentVal);
                                if (result) {
                                  Object.entries(result).forEach(([key, value]) => {
                                    handleItemChange({ target: { name: key, value } });
                                  });
                                } else {
                                  toast.info("No data found for this SKU");
                                }
                              }
                            }}
                            required={field.required}
                            style={
                              (field.readOnly || readOnly)
                                ? { pointerEvents: "none", backgroundColor: "#f8f9fa" }
                                : {}
                            }
                          />
                          {itemErrors[field.name] && (
                            <div className="invalid-feedback d-block">
                              {itemErrors[field.name]}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <label className="form-label" htmlFor={`item-${field.name}`}>
                            {field.label} {field.required && <span className="text-danger">*</span>}
                          </label>
                          <div className={field.name === "orderQty" && blindSerialEnabled ? "input-group" : undefined}>
                            <input
                              id={`item-${field.name}`}
                              type={field.type}
                              ref={attachRef}
                              className={`form-control ${(field.readOnly || readOnly) ? "bg-light" : ""} ${itemErrors[field.name] ? "is-invalid" : ""}`}
                              name={field.name}
                              value={itemFormData[field.name] ?? ""}
                              onChange={handleItemChange}
                              readOnly={field.readOnly || readOnly}
                              style={
                                (field.readOnly || readOnly)
                                  ? { pointerEvents: "none", backgroundColor: "#f8f9fa" }
                                  : {}
                              }
                            />
                            {field.name === "orderQty" && blindSerialEnabled && (
                              <SerialCaptureButton
                                captured={countCapturedSerials(itemFormData.serialDetails, itemFormData.orderQty, blindSerialColumns)}
                                qty={itemFormData.orderQty}
                                onClick={() => setSerialTarget({ kind: "blind" })}
                              />
                            )}
                          </div>
                          {itemErrors[field.name] && (
                            <div className="invalid-feedback d-block">
                              {itemErrors[field.name]}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="d-flex justify-content-end w-100 mt-3 mb-4">
                <button type="button" className="btn btn-primary" onClick={addOrUpdateItemToTable}>
                  {editingItem ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* SKUSearchResults for PO/SKU searches (non-BLIND groups) */}
      {hasNonBlindGroups && (
        <SKUSearchResults
          searchResults={loadTable}
          onDispatchQtyChange={(skuId, index, value, fieldName) => {
            handleDispatchQtyChange(skuId, index, value, fieldName);
          }}
          onRemoveSku={(skuId) => {
            setSkuToRemove(skuId);
            setOpenConfirm(true);
          }}
          onSelectionChange={handleSelectionChange}

          readOnly={viewOnly}
          showPriceStatus={isEdit || viewOnly}
          forceEnableAddItems={Number(formData.createUsing) === 3}
          defaultBlindGroupKey="BLIND"
          showGRExtraColumns={true}

          isAddDetailsMode={isAddDetailsMode} 
          hideQtyColumns={hideQtyColumnsForCreateByPO} 
          onCaptureSerials={(groupKey, index) => setSerialTarget({ kind: "row", groupKey, index })}
        />
      )}

      <SerialCaptureModal
        show={!!serialModalProps}
        onHide={() => setSerialTarget(null)}
        qty={serialModalProps?.qty}
        columns={serialModalProps?.columns || []}
        initialRows={serialModalProps?.rows || []}
        subtitle={serialModalProps?.skuId}
        readOnly={viewOnly || isAddDetailsMode || !!serialModalProps?.readOnly}
        onSave={handleSerialSave}
      />

      {/* LineItemsTable (flat) - show only BLIND rows (or BLIND mode) */}
      {shouldShowLineItems && (
        <LineItemsTable
          data={flattenedLoadRows.filter(r => r.__groupKey === 'BLIND')}
          onEdit={(row) => handleEditItem(row)}
          onDelete={(row) => handleDeleteItem(row)}
          onViewSerials={handlePreviewSerials}
          readOnly={viewOnly}
        />
      )}

      {/* Footer */}
      <div className="d-flex justify-content-center gap-2 p-3">
        <button className="btn btn-secondary" onClick={onCancel}>Back</button>
        {!viewOnly && <button className="btn btn-primary" onClick={handleSubmit}>{isAddDetailsMode ? 'Submit Details' : isEdit ? 'Update ASN' : 'Create ASN'}</button>}
        {!isEdit && !isAddDetailsMode && formData.receiptStatus === "Pending" && formData.asnNumber != null && (
          <button className="btn btn-success ms-2" onClick={handleConfirmAsn}>Confirm ASN</button>
        )}
      </div>
    </>
  );
}

